import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { Formik } from "formik";
import { postAPICall } from "lib/callApi";
import { CREATE_USER_REVENUE } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import { formatNumberWithComma } from "utils/HelperFunctions";
import * as Yup from "yup";

// Define Yup validation schema
const validationSchema = Yup.object().shape({
  dealName: Yup.string().matches(/^[a-zA-Z0-9\s]+$/, "Deal Name must not contain weird symbols"),
  revenue: Yup.string()
    .required("Revenue is required")
    .test("is-number", "Revenue must be a number", (value) => {
      if (!value) return true;
      const stringNumWithNoCommas = value.replace(/,/gi, "");

      // Make sure there is no comma in the string and it is a number
      return !Number.isNaN(parseFloat(stringNumWithNoCommas));
    })
    .test("greater-than-0", "Revenue must be greater than 0", (value) => {
      if (!value) return true;
      const stringNumWithNoCommas = value.replace(/,/gi, "");
      return parseFloat(stringNumWithNoCommas) > 0;
    }),
});

function RevenueInputForm({ fetchRevenueData, handleOpenLedgerModal }) {
  const handleRevenueChange = async (data, resetForm) => {
    try {
      // Make sure to remove commas and convert to number before saving into the DB
      const newRevenue = parseFloat(data.revenue.replace(/,/gi, ""));
      const isPositive = newRevenue >= 0;
      const dealName = data?.dealName ? data?.dealName : null;

      const response = await postAPICall(CREATE_USER_REVENUE, true, {
        newRevenue,
        isPositive,
        dealName,
      });

      // If the revenue update succeed, fetch again to get latest number
      if (response?.data?.success) {
        fetchRevenueData();
        // Call the parent function to update ledger data
        handleOpenLedgerModal();
        resetForm();
      }
    } catch (err) {
      console.log(err);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <MDBox>
      <Formik
        onSubmit={(values, { resetForm }) => handleRevenueChange(values, resetForm)}
        initialValues={{ revenue: "", dealName: "" }}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          isSubmitting,
          errors,
          touched,
          setFieldValue,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit}>
            <MDBox
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                justifyContent: "space-between",
              }}
            >
              <MDBox
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  justifyContent: "space-between",
                }}
              >
                <MDInput
                  id="dealName"
                  name="dealName"
                  label="Deal Name (Optional)"
                  value={values.dealName}
                  type="text"
                  onChange={handleChange}
                  error={touched.dealName && !!errors.dealName}
                  helperText={touched.dealName && errors.dealName}
                  onBlur={handleBlur}
                  sx={{
                    width: {
                      xs: "193px",
                      sm: "140px",
                      md: "180px",
                    },
                    mr: 1,
                    height: "40px",
                  }}
                />
                <MDInput
                  id="revenue"
                  name="revenue"
                  label="Amount (USD) *"
                  value={values.revenue}
                  onChange={(event) => {
                    const value = formatNumberWithComma(event.target.value);
                    setFieldValue("revenue", value);
                  }}
                  error={touched.revenue && !!errors.revenue}
                  helperText={touched.revenue && errors.revenue}
                  onBlur={handleBlur}
                  sx={{
                    width: {
                      xs: "193px",
                      sm: "100px",
                      md: "140px",
                    },
                    height: "40px",
                    mt: {
                      xs: 2,
                      sm: 0,
                    },
                  }}
                />
              </MDBox>
              <MDBox
                sx={{
                  mt: {
                    xs: 3,
                    sm: 0,
                  },
                }}
              >
                <MDButton
                  type="button"
                  color="gray80"
                  sx={{ mr: 1, height: "43px" }}
                  onClick={resetForm}
                >
                  Cancel
                </MDButton>
                <MDButton
                  color="primaryRed"
                  type="submit"
                  disabled={
                    isSubmitting || !values.revenue || !!errors.dealName || !!errors.revenue
                  }
                  sx={{ height: "43px" }}
                >
                  Submit
                </MDButton>
              </MDBox>
            </MDBox>
          </form>
        )}
      </Formik>
    </MDBox>
  );
}

export default RevenueInputForm;
